// API 统一出口
import user from './modules/user'
import common from './modules/common'
import request from '@/utils/request'
import { pkApiRequest, wrapPkSubmitBody } from '@/utils/pkWxCrypto'
import axios from 'axios'
import { showToast } from 'vant'
import UserManager from '@/store'
import { handleTokenExpired } from '@/utils/auth'
import { getLoginPathForSessionExpiry } from '@/utils/loginRedirect'

// 开发模式：前端本地调试时跳过真实登录与用户信息请求
// 注意：只在本地开发/测速时开启，上线务必改回 false
const DEV_MODE = false

// 解决重复声明 wxApi 的问题
// 将两个 wxApi 合并为一个
export const wxApi = {
  // 2025-05-27 恢复：需要用这个方法生成微信授权URL，redirect_uri指向统一授权服务
  // 获取授权URL
  getAuthUrl: (redirectUri) => {
    return request({
      url: '/hz/wx/oauth/getAuthUrl',
      method: 'get',
      params: { redirectUri }
    })
  },

  /* 2025-05-27 注释：改用统一授权服务后，不再需要后端生成授权URL，暂时注释保留
  // 获取授权URL
  getAuthUrl: (redirectUri) => {
    return request({
      url: '/hz/wx/oauth/getAuthUrl',
      method: 'get',
      params: { redirectUri }
    })
  },
  */

  // 获取access_token
  getAccessToken: (code) => {
    return request({
      url: '/hz/wx/oauth/getAccessToken',
      method: 'get',
      params: { code }
    })
  },

  // 获取用户信息
  getUserInfo: (accessToken, openid) => {
    return request({
      url: '/hz/wx/oauth/getUserInfo',
      method: 'get',
      params: { accessToken, openid }
    })
  },

  // 微信登录
  wxLogin: (data) => {
    const { openid, ...userInfo } = data;
    return request({
      url: '/hz/wx/user/login',
      method: 'post',
      params: { openid },
      data: userInfo
    })
  },
  
  // 添加 JS-SDK 配置接口
  getWxJsConfig: (data) => {
    return request({
      url: '/hz/wx/jsapi/config',
      method: 'get',
      params: data
    })
  },

  // 更新用户详细信息
  updateDetailInfo: (data) => {
    return request({
      url: '/hz/wx/user/info',
      method: 'put',
      data
    })
  }
}

// 根据环境设置基础URL
const baseUrl = process.env.VUE_APP_BASE_API || ''

// 创建axios实例
const service = axios.create({
  baseURL: baseUrl,
  timeout: 15000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 开发模式下：不自动带 token / 用户信息，避免触发后端鉴权
    if (DEV_MODE) {
      return config
    }

    // 如果有token则添加到header中（与 Pinia 一致；localStorage 兜底，避免偶发未同步）
    const token = UserManager.getToken() || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') || ''
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    
    // 添加用户信息
    const userInfo = UserManager.getUserInfo()
    if (userInfo && userInfo.openid) {
      config.headers['X-User-OpenId'] = userInfo.openid
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 如果返回的状态码表示 token 失效（通常是 401 或特定的业务码）
    if (res.code === 401 || res.code === 10001) {
      handleTokenExpired()
      return Promise.reject(new Error('登录已过期'))
    }
    
    // 如果返回的状态码不是200，说明接口请求有误
    if (res.code !== 200) {
      // 401: 未登录或token过期
      if (res.code === 401) {
        UserManager.logout()
        window.location.href = getLoginPathForSessionExpiry()
      } else {
        showToast({
          message: res.msg || '请求失败',
          type: 'fail'
        })
      }
      return Promise.reject(res)
    } else {
      return res
    }
  },
  error => {
    console.error('响应错误:', error)
    
    // 处理HTTP状态码错误
    if (error.response && error.response.status === 401) {
      handleTokenExpired()
      return Promise.reject(new Error('登录已过期'))
    }
    
    showToast({
      message: error.message || '网络错误',
      type: 'fail'
    })
    return Promise.reject(error)
  }
)

// 获取完整的视频URL
const getFullVideoUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('http')) return path
  const token = UserManager.getToken()
  // 在视频URL中添加token参数
  return `${baseUrl}${path}?token=${token}`
}

// 添加视频相关接口
const api = {
  // 微信相关接口
  wxApi,
  
  // 获取openId
  getOpenId: (code) => {
    return request({
      url: `/hz/wx/user/getOpenId`,
      method: 'get',
      params: { code }
    })
  },

  /** 活动大屏专用：配置文件账号，非 hz_users */
  loginActivityDashboardOnly: (data) => {
    return request({
      url: '/hz/wx/user/loginActivityDashboardOnly',
      method: 'post',
      data,
      timeout: 30000
    })
  },
  
  // 获取用户信息
  // 开发模式下返回本地假数据，不真正请求后端
  getUserInfo: () => {
    if (DEV_MODE) {
      return Promise.resolve({
        code: 200,
        data: {
          nickName: '开发用户',
          avatarUrl: '',
          gender: 0,
          city: '',
          province: '',
          country: '',
          openid: 'dev-openid'
        }
      })
    }
    return request({
      url: '/hz/wx/user/info',
      method: 'get'
    })
  },
  
  // 视频相关接口
  getRandomVideo: (type = '复活') => {
    return request({
      url: '/business/hz_videos/wx/random',
      method: 'get',
      params: { type }
    })
  },
  
  // 复活卡相关接口
  addReviveCard: () => {
    return request({
      url: '/hz/wx/user/addReviveCard',
      method: 'post'
    })
  },
  
  useReviveCard: () => {
    return request({
      url: '/hz/wx/user/useReviveCard',
      method: 'post'
    })
  },
  
  // 题目相关接口
  getRandomQuestions: (data) => {
    return request({
      url: '/business/hz_question_sets/wx/random',
      method: 'get',
      params: {
        companyName: data.companyName,
        quizType: data.quizType
      }
    })
  },

  // 根据关卡获取题目
  getQuestionsByLevel: (data) => {
    return request({
      url: '/business/hz_question_sets/wx/level',
      method: 'get',
      params: {
        level: data.level,
        companyName: data.companyName,
        quizType: data.quizType,
        ...(data.openid ? { openid: data.openid } : {})
      }
    })
  },

  // 检查关卡是否开放
  checkLevelAvailable: (data) => {
    return request({
      url: '/business/hz_question_sets/wx/checkLevelAvailable',
      method: 'get',
      params: {
        level: data.level,
        companyName: data.companyName,
        quizType: data.quizType,
        ...(data.openid ? { openid: data.openid } : {})
      }
    })
  },
  
  // 保存答题记录
  saveQuestionSetAnswers: (data) => {
    return request({
      url: '/business/hz_question_set_answers/wx/saveQuestionSetAnswers',
      method: 'post',
      data
    })
  },

  /** 闯关：校验通过后同事务写明细+排行榜+关卡（可选 level） */
  saveChallengeOutcome: (data) => {
    return request({
      url: '/business/hz_question_set_answers/wx/saveChallengeOutcome',
      method: 'post',
      data
    })
  },
  
  // 保存排行榜记录
  saveRankRecord: (data) => {
    return request({
      url: '/hz_rankings/hz_quiz_rankings/wx/saveRankRecord',
      method: 'post',
      data
    })
  },

  // 保存用户关卡得分详情
  saveLevelScore: (data) => {
    return request({
      url: '/hz_rankings/hz_level_scores/wx/saveLevelScore',
      method: 'post',
      data
    })
  },

  // 获取用户关卡得分详情
  getUserLevelScores: (openid) => {
    return request({
      url: '/hz_rankings/hz_level_scores/wx/getUserLevelScores',
      method: 'get',
      params: { openid }
    })
  },

  // 检查用户是否完成某个关卡
  checkLevelCompleted: (openid, level) => {
    return request({
      url: '/hz_rankings/hz_level_scores/wx/checkLevelCompleted',
      method: 'get',
      params: { openid, level }
    })
  },
  
  // 规则相关接口
  getRules: (type) => {
    return request({
      url: `/business/rules/wx/list`,
      method: 'get',
      params: { type }
    })
  },
  
  // PK对战相关接口（encrypted 模式自动加解密 payloadEnc）
  pk: {
    randomMatch: (data) => {
      return pkApiRequest({
        url: '/business/hz_pk_rooms/wx/random',
        method: 'get',
        params: data
      })
    },
    submitAnswer: (data) => {
      return pkApiRequest({
        url: '/business/hz_pk_rooms/wx/submitAnswer',
        method: 'post',
        data: wrapPkSubmitBody(data)
      })
    },
    getRoomInfo: (roomCode) => {
      return pkApiRequest({
        url: `/business/hz_pk_rooms/wx/room/${roomCode}`,
        method: 'get'
      })
    },
    quitRoom: (data) => {
      return request({
        url: '/business/hz_pk_rooms/wx/quit',
        method: 'post',
        data
      })
    },
    getRoomStatus: (roomCode, lastUpdateTime) => {
      return request({
        url: `/business/hz_pk_rooms/wx/status/${roomCode}`,
        method: 'get',
        params: { lastUpdateTime }
      })
    }
  },
  
  // 好友对战相关接口
  friendPk: {
    // 创建好友对战房间
    createRoom: (company) => {
      return pkApiRequest({
        url: '/business/hz_pk_rooms/wx/createRoom',
        method: 'post',
        data: { companyName: company }
      })
    },

    // 加入好友对战房间
    joinRoom: (roomCode) => {
      return pkApiRequest({
        url: '/business/hz_pk_rooms/wx/joinRoom',
        method: 'post',
        data: { roomCode }
      })
    },

    // 退出房间
    leaveRoom: (roomCode) => {
      return request({
        url: '/business/hz_pk_rooms/wx/quit',
        method: 'post',
        data: { roomCode }
      })
    },

    // 获取房间信息
    getRoomInfo: (roomCode) => {
      return pkApiRequest({
        url: `/business/hz_pk_rooms/wx/room/${roomCode}`,
        method: 'get'
      })
    },

    // 获取房间状态
    getRoomStatus: (roomCode, lastUpdateTime) => {
      return request({
        url: `/business/hz_pk_rooms/wx/status/${roomCode}`,
        method: 'get',
        params: { lastUpdateTime }
      })
    },

    // WebSocket相关方法
    getWsUrl: (roomCode, openid) => {
      // 与 VUE_APP_BASE_API 保持一致，避免部署在 /xcx 时仍连接旧的 /new/api。
      const apiBase = String(process.env.VUE_APP_BASE_API || '/xcx/api').replace(/\/+$/, '')
      const wsUrl = new URL(
        `${apiBase}/business/websocket/friend-pk/${roomCode}/${openid}`,
        window.location.origin
      )
      wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:'
      return wsUrl.toString()
    },

    // 发送答题消息
    sendAnswer: (data) => {
      return pkApiRequest({
        url: '/business/hz_pk_rooms/wx/submitAnswer',
        method: 'post',
        data: wrapPkSubmitBody(data)
      })
    },

    // 发送房间消息
    sendRoomMessage: (ws, type, data) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type,
          data
        }))
      } else {
        console.error('WebSocket未连接')
      }
    },

    // 开始心跳检测
    startHeartbeat: (ws) => {
      const heartbeatInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        } else {
          console.warn('WebSocket未连接，停止心跳')
          clearInterval(heartbeatInterval)
        }
      }, 30000) // 30秒发送一次心跳
      return heartbeatInterval
    },

    // 停止心跳检测
    stopHeartbeat: (timer) => {
      if (timer) {
        clearInterval(timer)
      }
    }
  },
  
  // 排行榜相关接口
  rankings: {
    /** type: quiz | pk | game | level1-5 | zc_total（找茬按 openid 汇总分，与 quiz 在前端合并） */
    getRankings: (type, limit) => {
      return request({
        url: `/wx/rankings/list/${type}`,
        method: 'get',
        params: { limit }
      })
    },

    getMyRank: (type, openid) => {
      return request({
        url: `/wx/rankings/rank/${type}/${openid}`,
        method: 'get'
      })
    },

    // 获取游戏排行榜
    getRankings_Game: (type, limit) => {
      return request({
        url: `/wx/rankings/list_Game/${type}`,
        method: 'get',
        params: {limit}
      })

    },

    // 获取公共排行榜（白名单接口，无需授权）
    getPublicRankings: (type, limit = 50) => {
      return request({
        url: `/wx/rankings/public/${type}`,
        method: 'get',
        params: { limit }
      })
    }

  },
  
  // 更新用户详细信息
  updateDetailInfo: (data) => {
    return request({
      url: '/hz/wx/user/updateDetailInfo',
      method: 'post',
      data
    })
  },
  
  // 退出登录
  logout: () => {
    return request({
      url: '/hz/wx/user/logout',
      method: 'post'
    })
  },

  // 获取微信配置
  getWxConfig: (data) => {
    return request({
      url: '/hz/wx/jsapi/config',
      method: 'get',
      params: data
    })
  },

  // 黄金矿工游戏相关接口
  getGameLevelDetail: (levelNum) => {
    return request({
      url: `/business/hz_game_level/detail/${levelNum}`,
      method: 'get'
    })
  },

  // 保存游戏得分
  saveGameScore: (data) => {
    return request({
      url: '/business/hz_game_records/save',
      method: 'post',
      data
    })
  },

  // 获取游戏排行榜
  getGameRankings: (limit = 10) => {
    return request({
      url: '/business/hz_game_rankings/list',
      method: 'get',
      params: { limit }
    })
  },

  // 检查用户今天是否可以答题
  checkCanAnswerToday: () => {
    return request({
      url: '/hz_rankings/hz_pk_answer_details/checkCanAnswerToday',
      method: 'get'
    })
  },
  
  // 检查用户是否可以观看视频
  checkCanWatchVideo: () => {
    return request({
      url: '/hz_rankings/hz_pk_answer_details/checkCanWatchVideo',
      method: 'get'
    })
  },

  /** H5 首页活动开放窗口（后台可配，东八区；免登录白名单） */
  getHomeActivityTimePublic: () => {
    return request({
      url: '/hz_rankings/hz_home_activity_time/public',
      method: 'get',
      timeout: 10000
    })
  },

  /**
   * H5 首页入口点击准入（须 JWT）：video | game | quiz
   * 窗口外仅返回错误，不封号；硬调业务接口才封。
   */
  checkActivityAccess: (feature) => {
    return request({
      url: '/hz_rankings/hz_home_activity_time/access',
      method: 'get',
      params: { feature },
      timeout: 10000
    })
  },

  // 视频观看相关接口
  video: {
    // 获取用户可观看的视频
    getWatchableVideo: (openid) => {
      return request({
        url: '/business/hz_videos/wx/getWatchableVideo',
        method: 'get',
        params: { openid }
      })
    },
    
    // 记录用户观看视频并加分
    recordWatch: (data) => {
      return request({
        url: '/business/hz_videos/wx/recordWatch',
        method: 'post',
        params: data
      })
    },
    
    // 查询用户观看记录
    getWatchRecords: (openid) => {
      return request({
        url: '/business/hz_videos/wx/watchRecords',
        method: 'get',
        params: { openid }
      })
    },

    /** 闯关复活：写 challenge_revive_watched_at；服务端在缺省时补 first_video_watch_time（不计分，供统计） */
    challengeReviveStatus: (openid) => {
      return request({
        url: '/business/hz_videos/wx/challengeRevive/status',
        method: 'get',
        params: { openid }
      })
    },
    challengeReviveMarkWatched: (data) => {
      return request({
        url: '/business/hz_videos/wx/challengeRevive/markWatched',
        method: 'post',
        params: data
      })
    },
    challengeReviveMarkExhausted: (data) => {
      return request({
        url: '/business/hz_videos/wx/challengeRevive/markExhausted',
        method: 'post',
        params: data
      })
    },

    /**
     * 2026 新增：H5 视频列表页（含封面），返回所有启用中的视频
     * 与原有 getWatchableVideo / recordWatch 解耦，仅用于"视频列表 -> 点卡片弹窗播放"场景
     */
    listForShow: () => {
      return request({
        url: '/business/hz_videos/wx/listForShow',
        method: 'get',
        timeout: 15000
      })
    },
    /** 水盾学习：视频完整播放后按视频加分（身份以 JWT 为准，openid 可不传） */
    awardStudyWatch: (data) => {
      return request({
        url: '/business/hz_videos/wx/awardStudyWatch',
        method: 'post',
        params: data,
        timeout: 15000
      })
    },
    /** 水盾学习：图文打开后标记已学习（不计分） */
    markStudyLearned: (data) => {
      return request({
        url: '/business/hz_videos/wx/markStudyLearned',
        method: 'post',
        params: data,
        timeout: 15000
      })
    },
    /** 水盾学习：已观看视频记录（身份以 JWT 为准） */
    studyWatchRecords: () => {
      return request({
        url: '/business/hz_videos/wx/studyWatchRecords',
        method: 'get',
        timeout: 15000
      })
    }
  },

  /** 线上活动统计大屏（需后台权限 token，如 loginByUserName） */
  activityReport: {
    /** 与后端 checkActivityReportAccess 一致，供路由守卫探测 */
    accessCheck: () => {
      return request({
        url: '/business/online_activity_report/access',
        method: 'get',
        timeout: 15000
      })
    },
    /** 风控封禁列表：渗透 ban + 登录失败短封（loginAccount / openid） */
    quizAbuseBans: () => {
      return request({
        url: '/business/online_activity_report/quizAbuseBans',
        method: 'get',
        timeout: 30000
      })
    },
    summaryByDay: (params) => {
      return request({
        url: '/business/online_activity_report/summaryByDay',
        method: 'get',
        params,
        timeout: 120000
      })
    },
    quizBatches: (params) => {
      return request({
        url: '/business/online_activity_report/quizBatches',
        method: 'get',
        params,
        timeout: 60000
      })
    },
    zcPlaySessions: (params) => {
      return request({
        url: '/business/online_activity_report/zcPlaySessions',
        method: 'get',
        params,
        timeout: 60000
      })
    },
    /** 合成排行榜：答题分+找茬分+总分，与首页「线上活动」口径一致 */
    compositeRanking: (params) => {
      return request({
        url: '/business/online_activity_report/compositeRanking',
        method: 'get',
        params,
        timeout: 120000
      })
    },
    /** 导出 Excel：返回 JSON msg 为服务端临时文件名，再请求 /common/download */
    exportQuizAnswersExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportQuizAnswersExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    },
    exportQuestionStatsExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportQuestionStatsExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    },
    exportQuestionOptionDistExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportQuestionOptionDistExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    },
    exportZcPlaySessionsExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportZcPlaySessionsExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    },
    exportZcAllLevelsPassUsersExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportZcAllLevelsPassUsersExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    },
    exportCompositeRankingExcel: (params) => {
      // return request({
      //   url: '/business/online_activity_report/exportCompositeRankingExcel',
      //   method: 'get',
      //   params,
      //   timeout: 120000
      // })
    }
  },

  /**
   * 2026 五周年问卷调查（H5 匿名提交，免登录）
   * 与 hz_videos / hz_rankings 等模块解耦，单独一组接口
   */
  /**
   * 2026 五周年限时促销海报（H5 公开）— 活动暂未开放
   */
  poster: {
    wxList: () => {
      // return request({ url: '/business/hz_posters/wx/list', method: 'get' })
    }
  },

  /** H5 首页主 Banner（公开） */
  homeBanner: {
    wxList: () => request({ url: '/business/hz_home_banners/wx/list', method: 'get' })
  },

  /**
   * 2026 五周年五盾打卡积分 + 活动配置 — 活动暂未开放
   * 开放后：看完直播/学习/问卷/海报/闯关成功后调 task.award 入账
   */
  task: {
    /** 加分入口；taskType: live|game|study|survey|poster */
    award: (payload) => {
      // return request({
      //   url: '/business/hz_user_task_score/wx/award',
      //   method: 'post',
      //   data: payload,
      //   timeout: 15000
      // })
    },
    myProgress: (openid) => {
      return request({
        url: '/business/hz_user_task_score/wx/myProgress',
        method: 'get',
        params: openid ? { openid } : {},
        timeout: 10000
      })
    },
    /** 拉所有活动配置（直播外链 等），Map<key,value> */
    // loadConfig: () => request({
    //   url: '/business/hz_user_task_score/wx/config',
    //   method: 'get'
    // }),
    /** 五周年活动榜 Top N（只读；视频学习分写入 hz_user_task_score） */
    rankings: (limit = 300) => request({
      url: '/business/hz_user_task_score/wx/rankings',
      method: 'get',
      params: { limit }
    }),
    /** 五周年活动榜我的名次（只读；身份以 JWT 为准） */
    myRank: () => request({
      url: '/business/hz_user_task_score/wx/myRank',
      method: 'get'
    })
  },

  /** 积分兑换商城 */
  pointsRedeem: {
    list: (category) => request({
      url: '/business/hz_point_prizes/wx/list',
      method: 'get',
      params: category ? { category } : {},
      timeout: 15000
    }),
    summary: () => request({
      url: '/business/hz_point_redeem/wx/summary',
      method: 'get',
      timeout: 10000
    }),
    redeem: (data) => request({
      url: '/business/hz_point_redeem/wx/redeem',
      method: 'post',
      data,
      timeout: 15000
    })
  },

  survey: {
    /**
     * 拉取问卷（含所有启用题目 + 选项）
     * 后端走 hz_survey 表，所以题目可以随时在后台改
     */
    wxLoad: (surveyCode = 'anniv5') => {
      // return request({
      //   url: '/business/hz_survey/wx/load',
      //   method: 'get',
      //   params: { surveyCode }
      // })
    },
    /** 提交问卷（含主表基础信息 + 明细 items） */
    wxSubmit: (data) => {
      // return request({
      //   url: '/business/hz_survey_answers/wx/submit',
      //   method: 'post',
      //   data,
      //   timeout: 30000
      // })
    },
    /** 探测某 openid 是否已提交过该问卷（可选，进入页面时提示） */
    wxExists: (openid, surveyCode = 'anniv5') => {
      // return request({
      //   url: '/business/hz_survey_answers/wx/exists',
      //   method: 'get',
      //   params: { openid, surveyCode }
      // })
    }
  },

  /** 隐患点举报（H5，需登录） */
  hiddenDanger: {
    wxSubmit: (data) => {
      // return request({
      //   url: '/business/hz_hidden_danger_report/wx/submit',
      //   method: 'post',
      //   data
      // })
    },
    wxMyList: (params) => {
      // return request({
      //   url: '/business/hz_hidden_danger_report/wx/myList',
      //   method: 'get',
      //   params
      // })
    }
  }
}

export default api 
